import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  FolderLocationInfo,
  MultiLocationScheduleConfig,
  ScheduleSelection,
  WorkScheduleType
} from '@services/work-schedule/model';
import {
  WorkScheduleService
} from '@services/work-schedule/work-schedule.service';

/**
 * Component for editing per-location schedule configurations.
 * Shows a global default schedule section and location-specific override sections.
 */
@Component({
  selector: 'app-multi-location-schedule-editor',
  templateUrl: './multi-location-schedule-editor.component.html',
  styleUrls: ['./multi-location-schedule-editor.component.scss'],
  standalone: false
})
export class MultiLocationScheduleEditorComponent implements OnInit, OnChanges {
  // Location data
  @Input() locations: FolderLocationInfo[] = [];

  // Current configuration
  @Input() config: MultiLocationScheduleConfig;

  // Inherited schedule info
  @Input() inheritedWorkDaysScheduleID: number = 0;
  @Input() inheritedWorkDaysName: string = '';
  @Input() inheritedDaysOffScheduleID: number = 0;
  @Input() inheritedDaysOffName: string = '';

  // Effective schedule IDs (resolved through inheritance chain - used for calendar preview)
  @Input() effectiveWorkDaysScheduleID: number = 0;
  @Input() effectiveDaysOffScheduleID: number = 0;

  // Subject info for fetching subject-specific schedule entries
  @Input() subjectType: 'folder' | 'asset' | 'team' = 'folder';
  @Input() subjectID: number = 0;

  // Output when configuration changes
  @Output() configChanged = new EventEmitter<MultiLocationScheduleConfig>();

  // Schedule options for dropdowns
  workDaysScheduleOptions: any[] = [];
  daysOffScheduleOptions: any[] = [];

  // Form state
  globalWorkDaysID: number = -1;
  globalDaysOffID: number = -1;
  locationSchedules: { [locationID: string]: ScheduleSelection } = {};

  // Calendar preview state
  selectedPreviewLocationID: number = 0;  // 0 = all locations

  // Sorted locations for display (alphabetically by name)
  sortedLocations: FolderLocationInfo[] = [];

  // Cached preview IDs to avoid recalculation on every change detection
  cachedPreviewWorkDaysID: number = 0;
  cachedPreviewDaysOffID: number = 0;

  // Default schedule selection (reused to avoid creating new objects)
  private readonly defaultScheduleSelection: ScheduleSelection = { workDaysScheduleID: -1, daysOffScheduleID: -1 };

  constructor(
    private translate: TranslateService,
    private workScheduleService: WorkScheduleService
  ) {}

  ngOnInit() {
    this.sortLocations();
    this.buildScheduleOptions();
    this.initializeFormState();
    this.updateCachedPreviewIDs();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['locations']) {
      this.sortLocations();
    }
    if (changes['config'] && !changes['config'].firstChange) {
      this.initializeFormState();
      this.updateCachedPreviewIDs();
    }
    // Update cached preview IDs when effective schedule IDs change
    if (changes['effectiveWorkDaysScheduleID'] || changes['effectiveDaysOffScheduleID']) {
      this.updateCachedPreviewIDs();
    }
  }

  /**
   * Sort locations alphabetically by name
   */
  private sortLocations() {
    this.sortedLocations = [...this.locations].sort((a, b) =>
      (a.locationName || '').localeCompare(b.locationName || '')
    );
  }

  /**
   * Update cached preview IDs - call this whenever values that affect preview change
   */
  private updateCachedPreviewIDs() {
    this.cachedPreviewWorkDaysID = this.calculatePreviewWorkDaysID();
    this.cachedPreviewDaysOffID = this.calculatePreviewDaysOffID();
  }

  private buildScheduleOptions() {
    const workDaysSchedules = this.workScheduleService.getWorkSchedulesByType(WorkScheduleType.Work_Days);
    const daysOffSchedules = this.workScheduleService.getWorkSchedulesByType(WorkScheduleType.Days_Off);

    const locationDefaultLabel = this.translate.instant('WORK_SCHEDULE.Use_Location_Default');

    // Build work days options
    const inheritedWorkDaysName = this.inheritedWorkDaysName ||
      this.translate.instant('WORK_SCHEDULE.Location_Default');
    this.workDaysScheduleOptions = [
      { scheduleID: -1, name: locationDefaultLabel },
      ...workDaysSchedules
    ];

    // Build days off options
    const inheritedDaysOffName = this.inheritedDaysOffName ||
      this.translate.instant('WORK_SCHEDULE.Location_Default');
    this.daysOffScheduleOptions = [
      { scheduleID: -1, name: locationDefaultLabel },
      ...daysOffSchedules
    ];
  }

  private initializeFormState() {
    if (this.config) {
      // Load from existing config
      this.globalWorkDaysID = this.config.global?.workDaysScheduleID ?? -1;
      this.globalDaysOffID = this.config.global?.daysOffScheduleID ?? -1;
      this.locationSchedules = { ...this.config.locations };
    } else {
      // Default state: everything inherits from location
      this.globalWorkDaysID = -1;
      this.globalDaysOffID = -1;
      this.locationSchedules = {};
    }

    // Ensure each location has a schedule entry
    for (const location of this.locations) {
      if (!this.locationSchedules[location.locationID]) {
        this.locationSchedules[location.locationID] = {
          workDaysScheduleID: -1,
          daysOffScheduleID: -1
        };
      }
    }
  }

  onGlobalWorkDaysChange() {
    this.updateCachedPreviewIDs();
    this.emitConfigChange();
  }

  onGlobalDaysOffChange() {
    this.updateCachedPreviewIDs();
    this.emitConfigChange();
  }

  onLocationWorkDaysChange(locationID: number) {
    this.updateCachedPreviewIDs();
    this.emitConfigChange();
  }

  onLocationDaysOffChange(locationID: number) {
    this.updateCachedPreviewIDs();
    this.emitConfigChange();
  }

  onPreviewLocationChange() {
    this.updateCachedPreviewIDs();
  }

  private emitConfigChange() {
    const config: MultiLocationScheduleConfig = {
      mode: 'per-location',
      global: {
        workDaysScheduleID: this.globalWorkDaysID,
        daysOffScheduleID: this.globalDaysOffID
      },
      locations: { ...this.locationSchedules }
    };
    this.configChanged.emit(config);
  }

  getLocationSchedule(locationID: number): ScheduleSelection {
    return this.locationSchedules[locationID] || this.defaultScheduleSelection;
  }

  setLocationWorkDays(locationID: number, value: number) {
    if (!this.locationSchedules[locationID]) {
      this.locationSchedules[locationID] = { workDaysScheduleID: -1, daysOffScheduleID: -1 };
    }
    this.locationSchedules[locationID].workDaysScheduleID = value;
    this.updateCachedPreviewIDs();
    this.emitConfigChange();
  }

  setLocationDaysOff(locationID: number, value: number) {
    if (!this.locationSchedules[locationID]) {
      this.locationSchedules[locationID] = { workDaysScheduleID: -1, daysOffScheduleID: -1 };
    }
    this.locationSchedules[locationID].daysOffScheduleID = value;
    this.updateCachedPreviewIDs();
    this.emitConfigChange();
  }

  /**
   * Calculate the effective work days schedule ID for calendar preview
   */
  private calculatePreviewWorkDaysID(): number {
    if (this.selectedPreviewLocationID > 0) {
      // Specific location preview
      const locSchedule = this.locationSchedules[this.selectedPreviewLocationID];
      if (locSchedule && locSchedule.workDaysScheduleID > 0) {
        return locSchedule.workDaysScheduleID;
      }
      // Fall back to global
      if (this.globalWorkDaysID > 0) {
        return this.globalWorkDaysID;
      }
    } else {
      // All locations preview - use global if set
      if (this.globalWorkDaysID > 0) {
        return this.globalWorkDaysID;
      }
    }
    // Fall back to effective schedule (resolved through inheritance chain)
    if (this.effectiveWorkDaysScheduleID > 0) {
      return this.effectiveWorkDaysScheduleID;
    }
    return 0;
  }

  /**
   * Calculate the effective days off schedule ID for calendar preview
   */
  private calculatePreviewDaysOffID(): number {
    if (this.selectedPreviewLocationID > 0) {
      const locSchedule = this.locationSchedules[this.selectedPreviewLocationID];
      if (locSchedule && locSchedule.daysOffScheduleID > 0) {
        return locSchedule.daysOffScheduleID;
      }
      if (this.globalDaysOffID > 0) {
        return this.globalDaysOffID;
      }
    } else {
      // All locations preview - use global if set
      if (this.globalDaysOffID > 0) {
        return this.globalDaysOffID;
      }
    }
    // Fall back to effective schedule (resolved through inheritance chain)
    if (this.effectiveDaysOffScheduleID > 0) {
      return this.effectiveDaysOffScheduleID;
    }
    return 0;
  }

  /**
   * Check if a location has any custom overrides (not using defaults)
   */
  hasLocationOverride(locationID: number): boolean {
    const schedule = this.locationSchedules[locationID];
    if (!schedule) return false;
    return schedule.workDaysScheduleID !== -1 || schedule.daysOffScheduleID !== -1;
  }

  /**
   * Reset a location to use defaults
   */
  resetLocationToDefaults(locationID: number) {
    this.locationSchedules[locationID] = {
      workDaysScheduleID: -1,
      daysOffScheduleID: -1
    };
    this.emitConfigChange();
  }
}
